import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Authority } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { AuthorityForm } from "../AuthorityForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListAuthorities.scss";
import { useLanguage } from "../../../../contexts";

const authorityController = new Authority();

export function ListAuthorities({ authoritiesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const [authorities,       setAuthorities]       = useState(null);
  const [authoritiesFilter, setAuthoritiesFilter] = useState([]);
  const [search,            setSearch]            = useState("");
  const [showModal,         setShowModal]         = useState(false);
  const [selected,          setSelected]          = useState(null);
  const [showConfirm,       setShowConfirm]       = useState(false);
  const [confirmMsg,        setConfirmMsg]        = useState("");
  const [isDelete,          setIsDelete]          = useState(false);
  const [rowMenu,           setRowMenu]           = useState(null); // { id, top, left, openUp } | null

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".authorities-list__row-menu, .authorities-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setAuthorities(null);
        const res = await authorityController.getAuthorities(accessToken, authoritiesActive);
        setAuthorities(res);
        setAuthoritiesFilter(res);
      } catch {
        setAuthorities([]);
        setAuthoritiesFilter([]);
      }
    })();
  }, [authoritiesActive, reload]);

  useEffect(() => {
    if (!authorities) return;
    if (!search) { setAuthoritiesFilter(authorities); return; }
    const q = search.toLowerCase();
    setAuthoritiesFilter(
      authorities.filter((d) =>
        d.code?.toLowerCase().includes(q) || d.name?.toLowerCase().includes(q)
      )
    );
  }, [search, authorities]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (d)  => { setSelected(d);   setShowModal(true); };

  const openToggle = (d) => {
    setSelected(d);
    setIsDelete(false);
    setConfirmMsg(`¿${d.active ? t("deactivate") : t("activate")} "${d.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (d) => {
    setSelected(d);
    setIsDelete(true);
    setConfirmMsg(`${t("are_you_sure_you_want_to_remove")} "${d.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await authorityController.updateAuthority(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? t("deactivated") : t("activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error")); }
  };

  const handleDelete = async () => {
    try {
      await authorityController.deleteAuthority(accessToken, selected._id);
      toast.success(t("deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error")); }
  };

  if (authorities === null) return <LoadingSpinner />;

  return (
    <div className="authorities-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="authorities-list__card">
        <div className="authorities-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-authority-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {authoritiesFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="authorities-list__tbl-scroll">
            <Table className="authorities-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {authoritiesFilter.map((d) => (
                  <Table.Row key={d._id}>
                    <Table.Cell data-label={t("code")}>{d.code}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{d.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="authorities-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="authorities-list__row-menu">
                        <button type="button" className="authorities-list__row-menu-btn" onClick={(e) => toggleRowMenu(d._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === d._id && ReactDOM.createPortal(
                        <div
                          className="authorities-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(d); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(d); setRowMenu(null); }}>
                            <Icon name={d.active ? "ban" : "check"} />{d.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="authorities-list__row-menu-item--danger" onClick={() => { openDelete(d); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selected ? `${t("edit")}: ${selected.name}` : t("new_authority")}
        size="small"
        className="catalog-modal"
      >
        <AuthorityForm
          authority={selected}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selected?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
